import { ColorButton } from "../../../../../shared/components/common/ColorButton";
import { EditorColor } from "@/types/common";
import { XIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface ColorPickerProps {
  className?: string;
  colors: EditorColor[];
  onChange: (color: string | null) => void;
  onClickOutside: () => void;
  position?: { top: number; left: number };
}

export type ColorType = "text" | "background";

export function ColorPicker({ className, colors, onChange, onClickOutside, position }: ColorPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState<{ top: number; left: number } | null>(null);
  const [isMeasured, setIsMeasured] = useState(false);

  function handleSelectColor(color: string | null) {
    onChange(color);
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!pickerRef.current?.contains(event.target as Node)) {
        onClickOutside();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClickOutside]);

  // Calculate adjusted position to keep picker on screen
  useEffect(() => {
    if (!position) {
      setAdjustedPosition(null);
      setIsMeasured(false);
      return;
    }

    // Use requestAnimationFrame to ensure the element has rendered and has dimensions
    const frameId = requestAnimationFrame(() => {
      if (!pickerRef.current) {
        setAdjustedPosition(position);
        setIsMeasured(true);
        return;
      }

      const pickerRect = pickerRef.current.getBoundingClientRect();
      const pickerWidth = pickerRect.width;
      const pickerHeight = pickerRect.height;

      // If dimensions are 0, element hasn't rendered yet, use position as-is but still mark as measured
      if (pickerWidth === 0 || pickerHeight === 0) {
        setAdjustedPosition(position);
        setIsMeasured(true);
        return;
      }

      let adjustedTop = position.top;
      let adjustedLeft = position.left;

      // Check if picker would go off bottom of screen
      if (position.top + pickerHeight > window.innerHeight) {
        adjustedTop = window.innerHeight - pickerHeight - 10; // 10px margin
      }

      // Check if picker would go off top of screen
      if (adjustedTop < 10) {
        adjustedTop = 10; // 10px margin from top
      }

      // Since the element is centered with translateX(-50%), we need to check bounds from center
      const halfWidth = pickerWidth / 2;

      // Check if picker would go off right side of screen (accounting for centered transform)
      if (position.left + halfWidth > window.innerWidth - 10) {
        adjustedLeft = window.innerWidth - halfWidth - 10; // 10px margin from right
      }

      // Check if picker would go off left side of screen (accounting for centered transform)
      if (position.left - halfWidth < 10) {
        adjustedLeft = halfWidth + 10; // 10px margin from left
      }

      setAdjustedPosition({ top: adjustedTop, left: adjustedLeft });
      setIsMeasured(true);
    });

    return () => cancelAnimationFrame(frameId);
  }, [position]);

  const pickerContent = (
    <div
      ref={pickerRef}
      className={clsx("bg-background border-default flex gap-2 rounded-lg border p-2 shadow-lg", className)}
      style={
        adjustedPosition
          ? {
              position: "fixed",
              top: `${adjustedPosition.top}px`,
              left: `${adjustedPosition.left}px`,
              transform: "translateX(-50%)",
              zIndex: 1000001,
            }
          : undefined
      }
    >
      {colors.map((color) => (
        <ColorButton
          aria-label={color.name}
          className={clsx("flex items-center justify-center", color.className)}
          key={color.name}
          onClick={() => handleSelectColor(color.value)}
        >
          {color.value === null ? <XIcon size={16} weight="bold" /> : null}
        </ColorButton>
      ))}
    </div>
  );

  // Only render if we have an adjusted position (positioned picker) or no position at all (unpositioned picker)
  if (position && !isMeasured) {
    // Render invisibly to measure dimensions
    return <div style={{ visibility: "hidden", position: "fixed", pointerEvents: "none" }}>{pickerContent}</div>;
  }

  // If positioned, render via portal
  if (adjustedPosition) {
    return createPortal(pickerContent, document.body);
  }

  return pickerContent;
}
